import { useNavigate, useParams } from "react-router-dom";
import { UserSearchItem } from "src/components";
import StoreSearchItem from "src/components/StoreSearchItem/StoreSearchItem";
import { UserSearch } from "src/containers";
import PreviousObservations from "src/containers/PreviousObservations/PreviousObservations";
import { useObservationFormQuery } from "src/hooks/useObservationForm";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import http from "src/libs/http";
import StoreSearch from "src/containers/StoreSearch/StoreSearch";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useUser } from "src/hooks";
import { AlertTriangle } from "lucide-react";
import Error404 from "src/pages/Error404/Error404";

const FormFinder = () => {
	const { formId: formIdString } = useParams<{ formId: string }>();
	const formId = parseInt(formIdString);

	const {
		error,
		data,
		isLoading: isFormLoading,
		isSuccess
	} = useObservationFormQuery(formId);

	const {
		refetch: refetchUser,
		data: userData,
		isFetching: isLoadingUser
	} = useUser(null, true, {
		enabled: false
	});

	const [selectedSearchItem, setSelectedSearchItem] =
		useState<Partial<StoreUser> | null>(null);

	const [missingRetailUserIdError, setMissingRetailUserIdError] =
		useState(false);

	useEffect(() => {
		if (data && data.limitToCurrentUser) {
			if (userData) {
				setSelectedSearchItem({
					id: userData.retail_user_id,
					uuid: userData.uuid,
					name: userData.name,
					role: userData.role
				});
			} else {
				refetchUser();
			}
		} else {
			setSelectedSearchItem(null);
		}
	}, [data]);

	useEffect(() => {
		if (data && data.limitToCurrentUser && userData) {
			// We need a retail user Id, if we can't get one we must throw an error
			if (!userData.retail_user_id) {
				setMissingRetailUserIdError(true);
				return;
			}

			setSelectedSearchItem({
				id: userData.retail_user_id,
				uuid: userData.uuid,
				name: userData.name,
				role: userData.role
			});
		}
	}, [userData]);

	const isLoading = isLoadingUser || isFormLoading;

	if (http.isAxiosError(error) && error.response?.status === 404) {
		return <Error404 />;
	}

	return (
		<div>
			{http.isAxiosError(error) && <p>{error.message}</p>}

			{missingRetailUserIdError && (
				<div className="mt-4">
					<div className="flex justify-center items-center flex-1">
						<div className="flex flex-col items-center gap-2">
							<AlertTriangle size={48} data-testid="warning-icon" />
							<div className="flex flex-col items-center gap-1 my-8">
								<p className="font-bold">Failed to get retail user Id</p>
								<p>Please contact support to continue with this observation.</p>
							</div>
						</div>
					</div>
				</div>
			)}

			{isLoading && (
				<div className="w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin" />
			)}

			{isSuccess && !isLoading && !missingRetailUserIdError && (
				<>
					<h1
						className={`text-lg font-semibold ${data.observationType === "GREAT_CONVERSATION" ? "mb-6" : "mb-8"}`}
					>
						{data.name}
					</h1>
					<div className="bg-white rounded-xl border border-border-light shadow-sm mt-6 mb-8 px-6 py-5">
						<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
							{checkObservationFormType(data.observationType) === "user" && (
								<div className="w-full">
									{selectedSearchItem && selectedSearchItem.id ? (
										<UserSearchItem
											user={selectedSearchItem as StoreUser}
											iconEvent={() => {
												if (!data?.limitToCurrentUser)
													setSelectedSearchItem(null);
											}}
											icon={
												data?.limitToCurrentUser ? undefined : <X size={24} />
											}
										/>
									) : (
										<UserSearch
											value={selectedSearchItem?.name}
											onChange={(user) => setSelectedSearchItem(user)}
										/>
									)}
								</div>
							)}

							{checkObservationFormType(data.observationType) === "store" && (
								<div className="w-full">
									{selectedSearchItem && selectedSearchItem.id ? (
										<StoreSearchItem
											store={selectedSearchItem as Store}
											iconEvent={() =>
												setSelectedSearchItem({ id: undefined, name: "" })
											}
											icon={<X size={24} />}
										/>
									) : (
										<StoreSearch
											value={selectedSearchItem?.name}
											onChange={(store) => setSelectedSearchItem(store)}
										/>
									)}
								</div>
							)}
						</div>
					</div>

					{selectedSearchItem && selectedSearchItem.id && (
						<PreviousObservations
							formId={formId}
							userId={
								checkObservationFormType(data.observationType) === "user"
									? String(selectedSearchItem.id)
									: undefined
							}
							storeId={
								checkObservationFormType(data.observationType) === "store"
									? Number(selectedSearchItem.id)
									: undefined
							}
							onResetSelectedSearchItem={
								data.limitToCurrentUser
									? undefined
									: () => setSelectedSearchItem(null)
							}
							observationType={data.observationType}
						/>
					)}
				</>
			)}
		</div>
	);
};

export default FormFinder;
